// Connectivity rules (md/CONNECTIVITY.md): src/sync/connectivity.js holds the state, src/iframe/legacyConnectivity.js
// wires it to the legacy. The modes are the Offline.js states the legacy still reads through
// public/m.eagle/js/connectivity.js:
//   'up'          online
//   'down'        network or API unreachable (browser event, probe)
//   'auto_down'   forced by a failing legacy XHR (Startup.js ajaxError)
//   'manual_down' chosen by the user, or forced after 3 failed synchronizations (eagle.js __xhrError)

export const MODE = Object.freeze({ UP: 'up', DOWN: 'down', AUTO_DOWN: 'auto_down', MANUAL_DOWN: 'manual_down' });

// Why a mode changed: 'network' detected (sessionHelper re-logs the user when it comes back up), 'manual' chosen
// by the user (back up: a partial evtManualSynch, no re-logon), 'api' a failing legacy XHR.
export const CAUSE = Object.freeze({ NETWORK: 'network', MANUAL: 'manual', API: 'api' });

export const isOnlineMode = (mode) => mode === MODE.UP;

// Offline.js markUp/markDown never overrode the user's choice (_state 'manual_down'), and a failing probe keeps
// an 'auto_down' as is: still offline, nothing new to tell the legacy.
export function modeAfterProbe(mode, reachable) {
	if (mode === MODE.MANUAL_DOWN) return mode;
	if (reachable) return MODE.UP;
	return mode === MODE.AUTO_DOWN ? mode : MODE.DOWN;
}

// Recovered by the periodic probe; the user's offline mode is only left on request.
export const shouldReprobe = (mode) => mode === MODE.DOWN || mode === MODE.AUTO_DOWN;

export const offlineAllowedFrom = (externis) => Boolean(externis?.offlineAllowed || externis?.partialOfflineAllowed);

// Main.js switchOffline() without argument (the chrome connection button). Going offline needs the offline
// permission, coming back online never does.
export function toggleAction(mode, offlineAllowed) {
	if (mode === MODE.UP) return offlineAllowed ? 'enter' : 'refuse';
	if (mode === MODE.DOWN) return 'check';
	return 'leave';
}

// Main.js switchOffline(state) called by the legacy itself: 'manual_down' (eagle.js __xhrError), 'auto_down'
// (Startup.js ajaxError) or 'up'.
export function requestAction(mode, requested, offlineAllowed) {
	if (requested === MODE.UP) {
		if (mode === MODE.UP) return 'none';
		return mode === MODE.DOWN ? 'check' : 'leave';
	}
	if (requested !== MODE.MANUAL_DOWN && requested !== MODE.AUTO_DOWN) return 'none';
	if (!offlineAllowed) return 'refuse';
	if (mode === MODE.MANUAL_DOWN || mode === requested) return 'none';
	return 'enter';
}

// sessionHelper.js onOfflineUp/onOfflineDown swal: detected changes only, and only when offline is allowed
// (otherwise the page is blocked instead).
export function transitionNotice(change, offlineAllowed) {
	if (!offlineAllowed || change?.cause !== CAUSE.NETWORK) return null;
	return isOnlineMode(change.state) ? 'up' : 'down';
}

// sessionHelper.js onOfflineDown blocked .page-container when offline is not allowed.
export const isOfflineBlocked = (mode, offlineAllowed) => !isOnlineMode(mode) && !offlineAllowed;

// sessionHelper.js checkForOffline(): the user left the app in manual offline mode.
export const shouldAskStayOffline = (stored, mode) => stored?.state === MODE.MANUAL_DOWN && mode !== MODE.MANUAL_DOWN;

// Main.js switchOffline(): toast shown when entering the manual offline mode with only the user's own locations.
export const partialOfflineNotice = (externis) => Boolean(externis?.partialOfflineAllowed && externis?.filterByOwnDeliveryLocations);
